import { faCode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import PlaceholderService from 'services/Placeholder.service';

/**
 * Componente de autocomplete para placeholders no editor
 * Detecta quando o usuário digita {{ e mostra sugestões de variáveis
 */
const PlaceholderAutocomplete = ({
  editorRef,
  onInsertPlaceholder,
  selectedTypes = []
}) => {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [placeholders, setPlaceholders] = useState({});
  const [autocompletePosition, setAutocompletePosition] = useState({
    top: 0,
    left: 0
  });
  const autocompleteRef = useRef(null);
  const isInitialized = useRef(false);
  const isKeyboardNavigation = useRef(false);

  // Carrega placeholders uma vez
  useEffect(() => {
    if (!isInitialized.current) {
      loadPlaceholders();
      isInitialized.current = true;
    }
  }, []);

  const loadPlaceholders = async () => {
    try {
      const response = await PlaceholderService.list();
      setPlaceholders(response);
    } catch (error) {
      console.error('Erro ao carregar placeholders:', error);
    }
  };

  // Converte placeholders agrupados em lista plana para autocomplete
  const flattenPlaceholders = () => {
    const flat = [];
    Object.entries(placeholders).forEach(([groupName, items]) => {
      Object.entries(items).forEach(([key, label]) => {
        flat.push({
          key,
          label,
          group: groupName,
          searchText: `${key} ${label}`.toLowerCase()
        });
      });
    });
    return flat;
  };

  // Filtra placeholders baseado no termo de busca
  const filterSuggestions = term => {
    if (!term) return [];
    const allPlaceholders = flattenPlaceholders();
    const lowerTerm = term.toLowerCase();
    return allPlaceholders
      .filter(p => p.searchText.includes(lowerTerm))
      .slice(0, 10); // Limita a 10 sugestões
  };

  // Detecta quando o usuário digita no editor
  useEffect(() => {
    if (!editorRef?.current) return;

    const getQuill = () => {
      if (editorRef.current?.getQuill) {
        return editorRef.current.getQuill();
      }
      const reactQuill = editorRef.current;
      return reactQuill?.getEditor?.();
    };

    const quill = getQuill();
    if (!quill) return;

    const handleTextChange = () => {
      // Usa false para não forçar o foco no editor ao verificar a seleção
      const range = quill.getSelection(false);
      if (!range) {
        setShowAutocomplete(false);
        return;
      }

      // Obtém o texto até a posição do cursor
      const text = quill.getText(0, range.index);

      // Procura por {{ mais próximo antes do cursor
      const lastOpenBrace = text.lastIndexOf('{{');
      const lastCloseBrace = text.lastIndexOf('}}', lastOpenBrace);

      // Se encontrou {{ e não há }} depois dele, mostra autocomplete
      if (lastOpenBrace !== -1 && lastOpenBrace > lastCloseBrace) {
        const searchText = text.substring(lastOpenBrace + 2, range.index);
        const filtered = filterSuggestions(searchText);

        if (filtered.length > 0) {
          setSearchTerm(searchText);
          setSuggestions(filtered);
          setSelectedIndex(0);
          setHoveredIndex(null);
          isKeyboardNavigation.current = false;
          setShowAutocomplete(true);

          // Calcula posição do autocomplete baseado na posição do cursor
          const editorElement = document.querySelector('.ql-editor');
          if (editorElement) {
            const bounds = quill.getBounds(range.index);
            const editorRect = editorElement.getBoundingClientRect();
            setAutocompletePosition({
              top: editorRect.top + bounds.top + bounds.height + 5,
              left: editorRect.left + bounds.left
            });
          }
        } else {
          setShowAutocomplete(false);
        }
      } else {
        setShowAutocomplete(false);
      }
    };

    // Escuta eventos de texto e seleção
    quill.on('text-change', handleTextChange);
    quill.on('selection-change', handleTextChange);

    return () => {
      quill.off('text-change', handleTextChange);
      quill.off('selection-change', handleTextChange);
    };
  }, [editorRef, placeholders]);

  // Handler para inserir placeholder selecionado
  const insertPlaceholder = placeholder => {
    if (!editorRef?.current) return;

    const getQuill = () => {
      if (editorRef.current?.getQuill) {
        return editorRef.current.getQuill();
      }
      const reactQuill = editorRef.current;
      return reactQuill?.getEditor?.();
    };

    const quill = getQuill();
    if (!quill) return;

    const range = quill.getSelection(true);
    if (!range) return;

    const text = quill.getText(0, range.index);
    const lastOpenBrace = text.lastIndexOf('{{');

    if (lastOpenBrace !== -1) {
      // Remove o texto desde {{ até o cursor
      quill.deleteText(lastOpenBrace, range.index - lastOpenBrace);

      // Insere o placeholder completo
      const placeholderText = `{{${placeholder.key}}}`;
      quill.insertText(lastOpenBrace, placeholderText);
      quill.setSelection(lastOpenBrace + placeholderText.length);

      if (onInsertPlaceholder) {
        onInsertPlaceholder(placeholderText);
      }
    }

    setShowAutocomplete(false);
    setHoveredIndex(null);
    isKeyboardNavigation.current = false;
  };

  // Handler para navegação com teclado
  useEffect(() => {
    if (!showAutocomplete) return;

    const handleKeyDown = e => {
      // Verifica se o editor está focado antes de processar
      const editorElement = document.querySelector('.ql-editor');
      if (!editorElement || editorElement !== document.activeElement) {
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        isKeyboardNavigation.current = true;
        setHoveredIndex(null);
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        isKeyboardNavigation.current = true;
        setHoveredIndex(null);
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'Enter' && suggestions[selectedIndex]) {
        e.preventDefault();
        e.stopPropagation();
        insertPlaceholder(suggestions[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [showAutocomplete, suggestions, selectedIndex]);

  // Fecha autocomplete ao clicar fora
  useEffect(() => {
    if (!showAutocomplete) return;

    const handleClickOutside = e => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(e.target) &&
        !document.querySelector('.ql-editor')?.contains(e.target)
      ) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAutocomplete]);

  if (!showAutocomplete || suggestions.length === 0) {
    return null;
  }

  return (
    <div
      ref={autocompleteRef}
      className="position-fixed bg-body border shadow-lg rounded"
      style={{
        top: `${autocompletePosition.top}px`,
        left: `${autocompletePosition.left}px`,
        zIndex: 9999,
        minWidth: '300px',
        maxWidth: '400px',
        maxHeight: '300px',
        overflowY: 'auto'
      }}
    >
      <div className="p-2 border-bottom">
        <small className="text-muted d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faCode} size="sm" />
          <span>Variáveis disponíveis</span>
        </small>
      </div>
      <div className="d-flex flex-column">
        {suggestions.map((suggestion, index) => {
          // Usa hoverIndex se estiver usando mouse, senão usa selectedIndex do teclado
          const isActive =
            hoveredIndex === index ||
            (hoveredIndex === null &&
              isKeyboardNavigation.current &&
              index === selectedIndex);

          return (
            <button
              key={`${suggestion.group}-${suggestion.key}`}
              type="button"
              className={`text-start border-0 p-2 ${
                isActive ? 'bg-body-secondary' : 'bg-transparent'
              }`}
              onClick={() => {
                insertPlaceholder(suggestion);
              }}
              onMouseEnter={() => {
                isKeyboardNavigation.current = false;
                setHoveredIndex(index);
              }}
              onMouseLeave={() => {
                setHoveredIndex(null);
              }}
              style={{
                cursor: 'pointer',
                transition: 'background-color 0.15s'
              }}
            >
              <div
                className={`small fw-medium ${isActive ? 'text-muted' : ''}`}
              >
                {suggestion.label}
              </div>
              <code className="text-muted" style={{ fontSize: '0.75rem' }}>
                {`{{${suggestion.key}}}`}
              </code>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PlaceholderAutocomplete;
